<template>
  <basic-page title="Tukiosaston Henkilöt" fluid>
    <v-row class="fill-height">
      <v-col cols="12" lg="10" xl="6" offset-xl="3" offset-lg="1">
        <v-card class="mt-5">
          <v-card-title>
            Tukiosaston Henkilöt
            <v-spacer />
            <v-btn color="primary" rounded @click="openDialog()">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Haku"
              single-line
              hide-details
              class="mb-4"
            ></v-text-field>
            <v-data-table
              :headers="headers"
              :items="personnel"
              :search="search"
              :loading="loading"
              :items-per-page="-1"
              hide-default-footer
            >
              <template v-slot:item.actions="{ item }">
                <v-icon small class="mr-2" @click="openDialog(item)">
                  mdi-pencil
                </v-icon>
                <v-icon small color="red" @click="deletePerson(item)">
                  mdi-delete
                </v-icon>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.vacancy"
                  label="Vakanssi"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.name"
                  label="Sukunimi Etunimi"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.phone"
                  label="Puhelinnumero"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">Peruuta</v-btn>
          <v-btn color="blue darken-1" text @click="save">Tallenna</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import { firestore as db } from "@/plugins/firebase.app";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

export default {
  name: "TukiosastonHenkilot",
  components: { BasicPage },
  data: () => ({
    dialog: false,
    loading: false,
    search: "",
    headers: [
      { text: "Vakanssi", value: "vacancy" },
      { text: "Nimi", value: "name" },
      { text: "Puhelin", value: "phone" },
      { text: "Toiminnot", value: "actions", sortable: false },
    ],
    personnel: [],
    editedIndex: -1,
    editedItem: {
      id: null,
      vacancy: "",
      name: "",
      phone: "",
    },
    defaultItem: {
      id: null,
      vacancy: "",
      name: "",
      phone: "",
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Uusi henkilö" : "Muokkaa henkilöä";
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      try {
        const querySnapshot = await getDocs(
          collection(db, "tukiosaston_henkilot")
        );
        this.personnel = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        this.personnel.sort((a, b) => {
          const nameA = (a.name || "").toString().toLowerCase();
          const nameB = (b.name || "").toString().toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;

          return 0;
        });
      } catch (error) {
        console.error("Error fetching personnel: ", error);
        this.$toast("Virhe haettaessa henkilöitä", { type: "error" });
      } finally {
        this.loading = false;
      }
    },

    openDialog(item) {
      if (item) {
        this.editedIndex = this.personnel.indexOf(item);
        this.editedItem = Object.assign({}, item);
      } else {
        this.editedIndex = -1;
        this.editedItem = Object.assign({}, this.defaultItem);
      }
      this.dialog = true;
    },

    async deletePerson(item) {
      if (
        await this.$confirm("Haluatko varmasti poistaa henkilön?", {
          title: "Poista henkilö",
          color: "error",
        })
      ) {
        try {
          await deleteDoc(doc(db, "tukiosaston_henkilot", item.id));
          const index = this.personnel.indexOf(item);
          this.personnel.splice(index, 1);
          this.$toast("Henkilö poistettu", { type: "success" });
        } catch (error) {
          console.error("Error deleting person: ", error);
          this.$toast("Virhe poistettaessa henkilöä", { type: "error" });
        }
      }
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    async save() {
      if (this.editedIndex > -1) {
        // Update
        try {
          const personRef = doc(db, "tukiosaston_henkilot", this.editedItem.id);
          await updateDoc(personRef, {
            vacancy: this.editedItem.vacancy,
            name: this.editedItem.name,
            phone: this.editedItem.phone || "",
          });
          Object.assign(this.personnel[this.editedIndex], this.editedItem);
          this.$toast("Tallennettu", { type: "success" });
        } catch (error) {
          console.error("Error updating person: ", error);
          this.$toast("Virhe tallennettaessa", { type: "error" });
        }
      } else {
        // Create
        try {
          const docRef = await addDoc(collection(db, "tukiosaston_henkilot"), {
            vacancy: this.editedItem.vacancy,
            name: this.editedItem.name,
            phone: this.editedItem.phone || "",
          });
          this.editedItem.id = docRef.id;
          this.personnel.push(this.editedItem);
          this.$toast("Luotu", { type: "success" });
        } catch (error) {
          console.error("Error adding person: ", error);
          this.$toast("Virhe luotaessa", { type: "error" });
        }
      }

      this.personnel.sort((a, b) => {
        const vacA = (a.vacancy || "").toString().toLowerCase();
        const vacB = (b.vacancy || "").toString().toLowerCase();
        if (vacA < vacB) return -1;
        if (vacA > vacB) return 1;

        const nameA = (a.name || "").toString().toLowerCase();
        const nameB = (b.name || "").toString().toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        return 0;
      });

      this.close();
    },
  },
};
</script>
